import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Flame, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelect } from "@/components/country-select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { categoriesQuery, categoryBoardQuery, listingBySlugQuery } from "@/lib/queries";
import { enrichListingFromUrl } from "@/lib/enrich.functions";
import { mirrorListingMedia } from "@/lib/listing-media.functions";

import { SocialLinks } from "@/components/socials";
import { LISTING_TYPES, formatUsd, labelForType, slugify } from "@/lib/behot";
import { GET_HOT_OG_IMAGE, GET_HOT_OG_IMAGE_ALT, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

type GetHotSearch = {
  listing?: string | undefined;
  category?: string | undefined;
};

export const Route = createFileRoute("/get-hot")({
  validateSearch: (search: Record<string, unknown>): GetHotSearch => ({
    listing: typeof search["listing"] === "string" ? search["listing"] : undefined,
    category: typeof search["category"] === "string" ? search["category"] : undefined,
  }),
  head: () =>
    buildPageMetadata({
      path: "/get-hot",
      title: "Get Hot — Submit Your Listing & Get Discovered | BeHot.Now",
      description:
        "Promote your startup, app, AI tool, music or creator profile: paste a link, we build the listing automatically, then add Heat and climb the live board.",
      keywords: [
        "submit your listing",
        "get discovered",
        "promote your project",
        "launch directory",
        "product listing platform",
        "boost visibility",
      ],
      ogTitle: "Get Hot — submit your listing",
      ogDescription: "Get on the board. Move up. Get seen.",
      image: GET_HOT_OG_IMAGE,
      imageAlt: GET_HOT_OG_IMAGE_ALT,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Get hot", path: "/get-hot" },
      ],
    }),
  component: GetHotPage,
});

function GetHotPage() {
  const { t } = useT();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { data: categories = [] } = useQuery(categoriesQuery);
  const { data: existing } = useQuery({
    ...listingBySlugQuery(search.listing ?? ""),
    enabled: Boolean(search.listing),
  });
  const enrich = useServerFn(enrichListingFromUrl);

  const [website, setWebsite] = useState("");
  const [fetching, setFetching] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<string>("creator");
  const [categoryId, setCategoryId] = useState<string>("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [brandColor, setBrandColor] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [socials, setSocials] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const selectedCategory = categories.find((c) => c.id === categoryId);
  const preselected = categories.find((c) => c.slug === search.category);
  const effectiveCategoryId = categoryId || preselected?.id || "";
  const effectiveCategory = selectedCategory ?? preselected;
  const { data: effectiveBoard, isLoading: boardLoading } = useQuery(
    categoryBoardQuery(effectiveCategoryId),
  );
  const boardIsEmpty =
    Boolean(effectiveCategoryId) && !boardLoading && (effectiveBoard?.length ?? 0) === 0;



  async function handleFetch() {
    if (!website.trim()) {
      toast.error(t("flow.getHot.toast.pasteLink"));
      return;
    }
    setFetching(true);
    try {
      const data = await enrich({ data: { url: website.trim() } });
      setWebsite(data.url);
      if (data.name && !name) setName(data.name);
      if (data.headline && !headline) setHeadline(data.headline);
      if (data.description && !description) setDescription(data.description.slice(0, 280));
      if (data.description && !longDescription) setLongDescription(data.description);
      if (data.logoUrl) setLogoUrl(data.logoUrl);
      if (data.coverUrl) setCoverUrl(data.coverUrl);
      if (data.brandColor) setBrandColor(data.brandColor);
      if (data.tags.length) setTags(data.tags);
      if (Object.keys(data.socials).length) setSocials(data.socials);
      toast.success(t("flow.getHot.toast.fetched"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("flow.getHot.toast.fetchError"));
    } finally {
      setFetching(false);
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!user) return;
    if (!name.trim() || !effectiveCategoryId) {
      toast.error(t("flow.getHot.toast.nameBoard"));
      return;
    }
    if (!country) {
      toast.error(t("flow.getHot.toast.selectCountry"));
      return;
    }
    setSubmitting(true);
    try {
      const link = website.trim();
      if (link) {
        const normalized = link
          .toLowerCase()
          .replace(/^https?:\/\/(www\.)?/, "")
          .replace(/\/+$/, "");
        const { data: clash } = await supabase
          .from("listings")
          .select("id,website_url")
          .in("moderation_status", ["draft", "pending", "approved"])
          .ilike("website_url", `%${normalized}%`)
          .limit(20);
        const duplicate = (clash ?? []).some(
          (l) =>
            (l.website_url ?? "")
              .toLowerCase()
              .replace(/^https?:\/\/(www\.)?/, "")
              .replace(/\/+$/, "") === normalized,
        );
        if (duplicate) {
          toast.error(t("flow.getHot.toast.duplicateLink"));
          setSubmitting(false);
          return;
        }
      }
      const { data: listing, error } = await supabase
        .from("listings")
        .insert({
          owner_user_id: user.id,
          name: name.trim(),
          slug: `${slugify(name)}-${Math.random().toString(36).slice(2, 6)}`,
          listing_type: type as (typeof LISTING_TYPES)[number],
          headline: headline.trim() || null,
          website_url: website.trim() || null,
          short_description: description.trim() || null,
          long_description: longDescription.trim() || null,
          city: city.trim() || null,
          country_code: country,
          logo_url: logoUrl.trim() || null,
          cover_url: coverUrl.trim() || null,
          brand_color: brandColor.trim() || null,
          tags,
          socials,
          moderation_status: "pending",
          is_public: false,
        })
        .select("id,slug")
        .single();
      if (error) throw error;

      const { error: linkError } = await supabase
        .from("listing_categories")
        .insert({ listing_id: listing.id, category_id: effectiveCategoryId });
      if (linkError) throw linkError;

      // Store our own copy of the fetched logo/cover so it never expires.
      try {
        await mirrorListingMedia({ data: { listingId: listing.id } });
      } catch {
        /* artwork mirroring is best-effort */
      }


      toast.success(t("flow.getHot.toast.created"));
      if (effectiveCategory?.slug) {
        void navigate({
          to: "/boost",
          search: { listing: listing.slug, category: effectiveCategory.slug },
        });
      } else {
        void navigate({ to: "/app/listings/$id", params: { id: listing.id } });
      }

    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      toast.error(
        /duplicate key|listings_unique_active_website/i.test(msg)
          ? t("flow.getHot.toast.duplicateLink")
          : msg || t("flow.getHot.toast.submitError"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <h1 className="font-display text-3xl font-bold sm:text-4xl">
        {t("flow.getHot.titlePrefix")} <span className="text-gradient-hot">{t("flow.getHot.titleWord")}</span>
      </h1>
      <p className="mt-2 text-muted-foreground">
        {t("flow.getHot.subtitle")}
      </p>

      {existing && (
        <div className="card-surface mt-6 flex flex-wrap items-center gap-3 p-4">
          <Flame className="size-5 text-primary" />
          <p className="min-w-0 flex-1 text-sm">
            {t("flow.getHot.existingCard.before")} <strong>{existing.name}</strong> {t("flow.getHot.existingCard.after")}
          </p>
          <Button asChild variant="hot" size="sm">
            <Link to="/p/$slug" params={{ slug: existing.slug }}>
              {t("flow.getHot.existingCard.addHeat")}
            </Link>
          </Button>
        </div>
      )}

      {!loading && !user && (
        <div className="card-surface mt-6 p-6 text-center">
          <p className="text-sm text-muted-foreground">{t("flow.getHot.signInPrompt")}</p>
          <Button asChild variant="hot" className="mt-4">
            <Link to="/login">{t("flow.getHot.signIn")}</Link>
          </Button>
        </div>
      )}

      {user && (
        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <div className="card-surface space-y-3 p-6">
            <Label htmlFor="website" className="flex items-center gap-2">
              <Sparkles className="size-4 text-primary" /> {t("flow.getHot.yourLink")}
            </Label>
            <div className="flex gap-2">
              <Input
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder={t("flow.getHot.linkPlaceholder")}
              />
              <Button type="button" variant="outline" onClick={handleFetch} disabled={fetching}>
                <Wand2 className="size-4" />
                {fetching ? t("flow.getHot.fetching") : t("flow.getHot.fetch")}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("flow.getHot.linkHelp")}
            </p>
            {(logoUrl || coverUrl) && (
              <div className="flex items-center gap-3 pt-1">
                {logoUrl && (
                  <img src={logoUrl} alt="" className="size-12 rounded-xl border border-border object-cover" />
                )}
                {coverUrl && (
                  <img src={coverUrl} alt="" className="h-12 w-24 rounded-lg border border-border object-cover" />
                )}
                <SocialLinks socials={socials} />
              </div>
            )}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {tags.map((tg) => (
                  <Badge key={tg} variant="outline">
                    {tg}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="card-surface space-y-4 p-6">
            <div className="space-y-2">
              <Label htmlFor="name">{t("flow.getHot.nameLabel")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("flow.getHot.namePlaceholder")}
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("flow.getHot.typeLabel")}</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LISTING_TYPES.map((tp) => (
                      <SelectItem key={tp} value={tp}>
                        {labelForType(tp)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t("flow.getHot.boardLabel")}</Label>
                <Select value={effectiveCategoryId} onValueChange={setCategoryId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("flow.getHot.pickBoard")} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="headline">{t("flow.getHot.headlineLabel")}</Label>
              <Input
                id="headline"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder={t("flow.getHot.headlinePlaceholder")}
                maxLength={90}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t("flow.getHot.shortDescLabel")}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                maxLength={280}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="long">{t("flow.getHot.fullDescLabel")}</Label>
              <Textarea
                id="long"
                value={longDescription}
                onChange={(e) => setLongDescription(e.target.value)}
                rows={6}
                placeholder={t("flow.getHot.fullDescPlaceholder")}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="city">{t("flow.getHot.cityLabel")}</Label>
                <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">{t("flow.getHot.countryLabel")}</Label>
                <CountrySelect id="country" value={country} onChange={setCountry} />
              </div>
            </div>
          </div>

          {effectiveCategory && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{t("flow.getHot.minBoostPrefix", { category: effectiveCategory.name })}</span>
              {boardIsEmpty &&
              effectiveCategory.opening_boost_cents < effectiveCategory.minimum_boost_cents ? (
                <>
                  <span className="line-through opacity-60">
                    {formatUsd(effectiveCategory.minimum_boost_cents)}
                  </span>
                  <strong className="text-foreground">
                    {formatUsd(effectiveCategory.opening_boost_cents)}
                  </strong>
                  <Badge variant="secondary">{t("flow.getHot.openingOffer")}</Badge>
                </>
              ) : (
                <strong className="text-foreground">
                  {formatUsd(effectiveCategory.minimum_boost_cents)}
                </strong>
              )}
              <span>{t("flow.getHot.forDays", { days: effectiveCategory.boost_duration_days })}</span>
            </p>
          )}


          <Button type="submit" variant="hot" size="lg" disabled={submitting} className="w-full">
            {submitting ? t("flow.getHot.submitting") : t("flow.getHot.submit")}
          </Button>
        </form>
      )}
    </div>
  );
}
